// src/components/dashboard/Workshop2Dashboard.js
// Professional Workshop 2 Dashboard using Tremor layout + ECharts
import React from 'react';
import {
  Card,
  Title,
  Text,
  Flex
} from '@tremor/react';
import { useTranslation } from 'react-i18next';
import EChart from './charts/EChart';
import DashboardGrid from './DashboardGrid';
import { W2_DEFAULT_LAYOUT } from './defaultLayouts';
import {
  NIVEAU_COLORS,
  buildCategoryBreakdown,
  buildMotivationRessourcesMatrix,
  buildRetainedNiveauMix,
  buildSourceEventMatrix
} from './charts/workshop2Data';

const NOT_RETAINED_COLOR = '#D1D5DB';

const Workshop2Dashboard = ({
  dreadedEvents = [],
  w2Sources = [],
  w2Mappings = [],
  customCategories = [],
  hiddenCategoryIds = [],
  w2Loading = false,
  editMode = false,
  layouts = null,
  onLayoutChange = null,
  onToggleHidden = null
}) => {
  const { t } = useTranslation();

  const translateFactor = (raw) => {
    const normalized = String(raw || '').toLowerCase();
    if (normalized.startsWith('elev')) {
      return t('workshop2.activity1.levelLabels.eleve');
    }
    if (normalized.startsWith('moyen')) {
      return t('workshop2.activity1.levelLabels.moyen');
    }
    if (normalized.startsWith('import')) {
      return t('workshop2.activity1.levelLabels.importantes');
    }
    if (normalized.startsWith('faible')) {
      return t('workshop2.activity1.levelLabels.faible');
    }
    return String(raw || '');
  };

  // Chart data (pure helpers, labels resolved via t())
  const categoryRows = buildCategoryBreakdown(w2Sources, { hiddenCategoryIds, customCategories, t });
  const diagnostic = buildMotivationRessourcesMatrix(w2Sources);
  const niveauMix = buildRetainedNiveauMix(w2Sources);
  const mapping = buildSourceEventMatrix(w2Sources, w2Mappings, dreadedEvents);

  const motivationLabels = [
    t('workshop2.activity1.levelLabels.faible'),
    t('workshop2.activity1.levelLabels.moyen'),
    t('workshop2.activity1.levelLabels.eleve')
  ];
  const ressourcesLabels = [
    t('workshop2.activity1.levelLabels.faibles'),
    t('workshop2.activity1.levelLabels.moyennes'),
    t('workshop2.activity1.levelLabels.importantes')
  ];
  const retainedLabel = t('dashboard.workshop2.retained');
  const notRetainedLabel = t('dashboard.workshop2.notRetained');

  // 3.1 Category overview — sorted horizontal bars, retained-stacked.
  // Color is keyed to category identity; retained/not-retained is the only status channel.
  const categoryOption = {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params) => {
        const row = categoryRows.find((entry) => entry.label === params[0].name);
        if (!row) {
          return params[0].name;
        }
        return `${row.label}<br />${retainedLabel}: ${row.retained}<br />${notRetainedLabel}: ${row.notRetained}<br />Total: ${row.total}`;
      }
    },
    legend: { bottom: 0, data: [retainedLabel, notRetainedLabel] },
    grid: { left: 8, right: 24, top: 8, bottom: 48, containLabel: true },
    xAxis: { type: 'value', minInterval: 1 },
    yAxis: {
      type: 'category',
      data: categoryRows.map((row) => row.label),
      inverse: true,
      axisLabel: { interval: 0 }
    },
    series: [
      {
        name: retainedLabel,
        type: 'bar',
        stack: 'total',
        data: categoryRows.map((row) => ({
          value: row.retained,
          itemStyle: { color: row.color }
        }))
      },
      {
        name: notRetainedLabel,
        type: 'bar',
        stack: 'total',
        data: categoryRows.map((row) => row.notRetained),
        itemStyle: { color: NOT_RETAINED_COLOR, borderRadius: [0, 6, 6, 0] }
      }
    ]
  };

  // 3.2 SR/OV diagnostic — motivation x ressources heatmap.
  // Cell color = dominating niveau, cell label = source count.
  const diagnosticData = diagnostic.data.map(([col, row, niveau]) => {
    const cell = diagnostic.cells[`${col}|${row}`];
    return { value: [col, row, niveau, cell ? cell.count : 0] };
  });
  const diagnosticOption = {
    tooltip: {
      trigger: 'item',
      formatter: (params) => {
        const [col, row] = params.value;
        const cell = diagnostic.cells[`${col}|${row}`];
        const header = `${motivationLabels[row]} • ${ressourcesLabels[col]}: ${cell ? cell.count : 0}`;
        if (!cell || cell.sources.length === 0) {
          return header;
        }
        const lines = cell.sources.map((entry) => (
          `${entry.name} (${translateFactor(entry.activite)} • ${t('dashboard.workshop2.niveau')} ${entry.niveau})`
        ));
        return `${header}<br />${lines.join('<br />')}`;
      }
    },
    grid: { left: 16, right: 16, top: 8, bottom: 64, containLabel: true },
    xAxis: {
      type: 'category',
      data: ressourcesLabels,
      name: t('dashboard.workshop2.axisRessources'),
      nameLocation: 'middle',
      nameGap: 36,
      nameTextStyle: { color: '#374151', fontWeight: 600, fontSize: 12 },
      axisLabel: { interval: 0 }
    },
    yAxis: {
      type: 'category',
      data: motivationLabels,
      name: t('dashboard.workshop2.axisMotivation'),
      nameLocation: 'middle',
      nameGap: 44,
      nameTextStyle: { color: '#374151', fontWeight: 600, fontSize: 12 },
      axisLabel: { interval: 0 }
    },
    visualMap: {
      type: 'piecewise',
      dimension: 2,
      orient: 'horizontal',
      left: 'center',
      bottom: 0,
      pieces: [
        { value: 1, color: NIVEAU_COLORS[1] },
        { value: 2, color: NIVEAU_COLORS[2] },
        { value: 3, color: NIVEAU_COLORS[3] }
      ]
    },
    series: [{
      type: 'heatmap',
      data: diagnosticData,
      label: {
        show: true,
        formatter: (params) => (params.value[3] > 0 ? String(params.value[3]) : ''),
        color: '#FFFFFF'
      },
      itemStyle: {
        color: (params) => (params.value[2] === 0 ? '#F3F4F6' : NIVEAU_COLORS[params.value[2]])
      },
      emphasis: { itemStyle: { shadowBlur: 8, shadowColor: 'rgba(0, 0, 0, 0.3)' } }
    }]
  };

  // 3.3 Retained niveau mix — donut, 3 slices off the canonical scorer.
  const niveauSlices = [
    { level: 1, name: t('dashboard.workshop2.lowRisk') },
    { level: 2, name: t('dashboard.workshop2.mediumRisk') },
    { level: 3, name: t('dashboard.workshop2.highRisk') }
  ];
  const niveauOption = {
    tooltip: { trigger: 'item' },
    title: {
      text: String(niveauMix.total),
      left: 'center',
      top: '38%',
      textStyle: { fontSize: 24, fontWeight: 'bold', color: '#111827' }
    },
    legend: {
      bottom: 0,
      data: niveauSlices.map((slice) => slice.name)
    },
    series: [{
      type: 'pie',
      radius: ['50%', '70%'],
      center: ['50%', '45%'],
      label: { show: false },
      data: niveauSlices.map((slice) => ({
        name: slice.name,
        value: niveauMix.counts[slice.level] || 0,
        itemStyle: { color: NIVEAU_COLORS[slice.level] }
      }))
    }]
  };

  // 3.4 Source x dreaded-event mapping — heatmap, zero strips pruned.
  const mappingOption = {
    tooltip: {
      trigger: 'item',
      formatter: (params) => {
        const sourceLabel = mapping.rowLabels[params.value[1]] || '';
        const eventLabel = mapping.columnLabels[params.value[0]] || '';
        return `${sourceLabel} • ${eventLabel}: ${params.value[2]}`;
      }
    },
    grid: { left: 8, right: 16, top: 8, bottom: 64, containLabel: true },
    xAxis: {
      type: 'category',
      data: mapping.columnLabels,
      axisLabel: { interval: 0, rotate: 30 }
    },
    yAxis: {
      type: 'category',
      data: mapping.rowLabels,
      axisLabel: { interval: 0 }
    },
    visualMap: {
      min: 0,
      max: Math.max(mapping.max, 1),
      calculable: true,
      orient: 'horizontal',
      left: 'center',
      bottom: 0,
      inRange: { color: ['#EEF2FF', '#6366F1'] }
    },
    series: [{
      type: 'heatmap',
      data: mapping.data,
      label: {
        show: true,
        formatter: (params) => (params.value[2] > 0 ? String(params.value[2]) : '')
      },
      emphasis: { itemStyle: { shadowBlur: 8, shadowColor: 'rgba(0, 0, 0, 0.3)' } }
    }]
  };

  if (w2Loading) {
    return (
      <Card className="!bg-white">
        <Flex justifyContent="center" alignItems="center" className="h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-orange-600 border-t-transparent mr-3"></div>
          <Text>{t('dashboard.status.loading')}</Text>
        </Flex>
      </Card>
    );
  }

  return (
    <div className="space-y-6 bg-gray-50 p-4 rounded-xl">
      {/* Charts Grid (draggable/resizable in edit mode) */}
      <DashboardGrid
        items={[
          {
            id: 'w2-categoryBars',
            fullscreenTitle: t('dashboard.workshop2.categoryOverview'),
            fullscreenContent: (<EChart option={categoryOption} height={560} />),
            element: (
              <Card className="!bg-white h-full flex flex-col">
                <Title className="!text-gray-900">{t('dashboard.workshop2.categoryOverview')}</Title>
                {categoryRows.length > 0 ? (
                  <div className="flex-1 min-h-0" style={{ minHeight: Math.max(90, categoryRows.length * 22 + 40) }}><EChart option={categoryOption} height="100%" /></div>
                ) : (
                  <div className="h-60 flex items-center justify-center">
                    <Text className="!text-gray-400">{t('dashboard.workshop2.noSources')}</Text>
                  </div>
                )}
              </Card>
            )
          },
          {
            id: 'w2-diagnosticHeatmap',
            fullscreenTitle: t('dashboard.workshop2.srDiagnostic'),
            fullscreenContent: (<EChart option={diagnosticOption} height={560} />),
            element: (
              <Card className="!bg-white h-full flex flex-col">
                <Title className="!text-gray-900">{t('dashboard.workshop2.srDiagnostic')}</Title>
                {diagnostic.total > 0 ? (
                  <div className="flex-1 min-h-0" style={{ minHeight: 95 }}><EChart option={diagnosticOption} height="100%" /></div>
                ) : (
                  <div className="h-60 flex items-center justify-center">
                    <Text className="!text-gray-400">{t('dashboard.workshop2.noSources')}</Text>
                  </div>
                )}
              </Card>
            )
          },
          {
            id: 'w2-niveauDonut',
            fullscreenTitle: t('dashboard.workshop2.niveauMix'),
            fullscreenContent: (<EChart option={niveauOption} height={560} />),
            element: (
              <Card className="!bg-white h-full flex flex-col">
                <Title className="!text-gray-900">{t('dashboard.workshop2.niveauMix')}</Title>
                <Text className="text-xs !text-gray-500 mt-1">{t('dashboard.workshop2.scorerNote')}</Text>
                {niveauMix.total > 0 ? (
                  <div className="flex-1 min-h-0" style={{ minHeight: 90 }}><EChart option={niveauOption} height="100%" /></div>
                ) : (
                  <div className="h-60 flex items-center justify-center">
                    <Text className="!text-gray-400">{t('dashboard.workshop2.noSources')}</Text>
                  </div>
                )}
              </Card>
            )
          },
          {
            id: 'w2-sourceEventMap',
            fullscreenTitle: t('dashboard.workshop2.sourceEventMap'),
            fullscreenContent: (<EChart option={mappingOption} height={560} />),
            element: (
              <Card className="!bg-white h-full flex flex-col">
                <Title className="!text-gray-900">{t('dashboard.workshop2.sourceEventMap')}</Title>
                <Text className="text-xs !text-gray-500 mt-1">
                  {t('dashboard.workshop2.sourceEventMapSubtitle', {
                    retained: mapping.retainedCount,
                    total: mapping.totalCount
                  })}
                </Text>
                {mapping.hasData ? (
                  <div className="flex-1 min-h-0" style={{ minHeight: Math.max(100, mapping.rowLabels.length * 20 + 45) }}><EChart
                    option={mappingOption}
                    height="100%"
                  /></div>
                ) : (
                  <div className="h-60 flex items-center justify-center">
                    <Text className="!text-gray-400">{t('dashboard.workshop2.noMappings')}</Text>
                  </div>
                )}
              </Card>
            )
          }
        ]}
        defaultLayout={W2_DEFAULT_LAYOUT}
        editMode={editMode}
        layouts={layouts}
        onLayoutChange={onLayoutChange}
        onToggleHidden={onToggleHidden}
      />
    </div>
  );
};

export default Workshop2Dashboard;
